'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type AdminRole = 'super_admin' | 'admin' | null;

interface AdminProfile {
  id: string;
  email: string;
  full_name: string;
  role: AdminRole;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [assignedStoreIds, setAssignedStoreIds] = useState<string[]>([]);
  const supabase = createClient();

  const fetchAdminProfile = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('admin_profiles')
        .select('id, email, full_name, role')
        .eq('id', userId)
        .single();

      if (profile) {
        setAdminProfile(profile as AdminProfile);

        // Only fetch assignments for regular admins
        if (profile.role === 'admin') {
          const { data: assignments } = await supabase
            .from('admin_store_assignments')
            .select('store_id')
            .eq('admin_id', userId);
          setAssignedStoreIds(assignments?.map((a: any) => a.store_id) ?? []);
        } else {
          // super_admin has access to all stores
          setAssignedStoreIds([]);
        }
      }
    } catch {
      // If admin_profiles table doesn't exist yet, fall back gracefully
      setAdminProfile(null);
      setAssignedStoreIds([]);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAdminProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAdminProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setAdminProfile(null);
        setAssignedStoreIds([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setAdminProfile(null);
    setAssignedStoreIds([]);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Derived role helpers
  const isSuperAdmin = adminProfile?.role === 'super_admin';
  const isAdmin = adminProfile?.role === 'admin';
  const adminRole: AdminRole = adminProfile?.role ?? null;

  const value = {
    user,
    session,
    loading,
    adminProfile,
    adminRole,
    isSuperAdmin,
    isAdmin,
    assignedStoreIds,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
